import { PRODUCT_PLACEMENT_OPTIONS } from "@/components/ProductPlacement/constants";
import { defaultRadioOption } from "@/app/(protected)/asset-generation/components/ExploreOptions/constant";
import { isImagenSupportedBrand } from "./brand.utils";
import {
  getBase64ByType,
  getBase64FormatForGeneratedImages,
  getBase64FormatForImages,
  getFileExtensionFromPath,
  gsGenericFilePattern,
} from "../utils";
import { getAspectRatio as getAspectRatioFromUtils } from "../aspectRatioUtils";

export const convertScriptDuration = (scriptDuration: string) => Number.parseInt(scriptDuration.replace(":", ""), 10) || 0;

export const getAspectRatio = (imageDimensions: string, videoScriptIncludeImage: boolean) => {
  // Regex to capture the aspect ratio inside the parentheses -> '1280 x 896 px (4 : 3)' -> '4:3'
  const regex = /\((\d+\s*:\s*\d+)\)/;
  const match = new RegExp(regex).exec(imageDimensions);
  const fallbackAspectRatio = videoScriptIncludeImage ? "1:1" : undefined;
  return match?.[1]?.replaceAll(/\s+/g, "") ?? fallbackAspectRatio;
};

export const convertCheckboxValues = (values: { visual?: string; script?: string }) => {
  const resolutionMap: Record<string, string> = {
    "1024px x 1024px": "1:1",
    "896px x 1152px": "3:4",
    "1152px x 896px": "4:3",
    "768px x 1344px": "9:16",
    "1344px x 768px": "16:9",
  };
  return {
    aspectRatio: values.visual ? resolutionMap[values.visual] || values.visual : undefined,
    scriptDuration: values.script ? Number.parseInt(values.script.split(":")[1], 10) : undefined, // Default duration
  };
};

const resolutionMapImagen: { [key: string]: { resolutionAndAspect: string; resolution: string } } = {
  "1:1": { resolutionAndAspect: "1024 x 1024 px (1 : 1)", resolution: "1024px x 1024px" },
  "3:4": { resolutionAndAspect: "896 x 1280 px (3 : 4)", resolution: "896px x 1280px" },
  "4:3": { resolutionAndAspect: "1280 x 896 px (4 : 3)", resolution: "1280px x 896px" },
  "9:16": { resolutionAndAspect: "768 x 1408 px (9 : 16)", resolution: "768px x 1408px" },
  "16:9": { resolutionAndAspect: "1408 x 768 px (16 : 9)", resolution: "1408px x 768px" },
};

const resolutionMapBria: { [key: string]: { resolutionAndAspect: string; resolution: string } } = {
  "1:1": { resolutionAndAspect: "1024 x 1024 px (1 : 1)", resolution: "1024px x 1024px" },
  "3:4": { resolutionAndAspect: "896 x 1152 px (3 : 4)", resolution: "896px x 1152px" },
  "4:3": { resolutionAndAspect: "1152 x 896 px (4 : 3)", resolution: "1152px x 896px" },
  "9:16": { resolutionAndAspect: "768 x 1344 px (9 : 16)", resolution: "768px x 1344px" },
  "16:9": { resolutionAndAspect: "1344 x 768 px (16 : 9)", resolution: "1344px x 768px" },
  "5:4": { resolutionAndAspect: "1088 x 896 px (5 : 4)", resolution: "1088px x 894px" },
};

export enum REQUESTED_FORMAT_TYPE {
  RESOLUTION_AND_ASPECT = "resolutionAndAspect",
  RESOLUTION = "resolution",
}

export const getResolutionByAspectRatio = (
  aspectRatio: string,
  format: REQUESTED_FORMAT_TYPE.RESOLUTION_AND_ASPECT | REQUESTED_FORMAT_TYPE.RESOLUTION = REQUESTED_FORMAT_TYPE.RESOLUTION_AND_ASPECT,
  selectedBrand?: string,
): string | undefined => {
  const isImagenBrand = isImagenSupportedBrand(selectedBrand ?? "");
  const resolutionMap = isImagenBrand ? resolutionMapImagen : resolutionMapBria;
  const entry = resolutionMap[aspectRatio];
  if (entry) {
    return format === REQUESTED_FORMAT_TYPE.RESOLUTION ? entry.resolution : entry.resolutionAndAspect;
  }
  return undefined;
};

export const getDefaultAspectRatio = (brand: string, projectType: string, ratio: string): string => {
  const aspectRatio = getAspectRatioFromUtils(brand, projectType);

  const filterDefaultRatio = aspectRatio?.filter((item) => item?.id?.includes(ratio));
  return filterDefaultRatio.length > 0 ? filterDefaultRatio[0]?.id : defaultRadioOption?.id;
};

export const getProductPlacementOptionIndexed = (index: number) => {
  return PRODUCT_PLACEMENT_OPTIONS.map((option) => {
    return {
      id: `${option.id}-${index}`,
      Label: option.label,
      ariaLabel: option.ariaLabel,
    };
  });
};

export const buildUploadErrorMessage = (error: string, label?: string, uploadText?: string, dragDropText?: string): string => {
  let message = error;
  if (label) {
    message += ` ${label}.`;
  }
  if (uploadText) {
    message += ` ${uploadText}`;
  }
  if (dragDropText) {
    message += ` ${dragDropText}`;
  }
  return message.trim();
};

export const parseInteger = (value: string): number => {
  if (!/^\d+$/.test(value)) {
    return Number.NaN;
  }
  return Number.parseInt(value, 10);
};

export const convertImagesToBase64 = async (image: string) => {
  const imageResponse = await getBase64FormatForImages({ pngURL: image });
  return imageResponse?.pngBase64 ?? "";
};

/**
 * Given a single image URL, returns the base64 image string if GCS, or the original URL string otherwise.
 * Supports PNG, JPEG, and PSD.
 * @param imageUrl - The image URL (can be gs:// or http(s)://)
 * @returns base64 string or original URL string
 */
export const getImageDataByUrl = async (imageUrl: string): Promise<string> => {
  // If GCS URL, extract extension and get base64; otherwise, return as-is
  if (gsGenericFilePattern.test(imageUrl)) {
    const ext = getFileExtensionFromPath(imageUrl)?.toLowerCase();
    if (!ext) throw new Error("Cannot determine file extension from GCS URL");
    return await getBase64ByType(imageUrl, ext);
  }
  return imageUrl;
};

export const getBase64Image = async (jpegURL: string, isNewGcsURLEnabled: boolean, projectId: string, imageType: string) => {
  let imageBase64;
  if (isNewGcsURLEnabled) {
    const { jpegBase64 } = await getBase64FormatForGeneratedImages({ jpegURL }, projectId, imageType);
    imageBase64 = jpegBase64;
  } else {
    const { jpegBase64 } = await getBase64FormatForImages({ jpegURL });
    imageBase64 = jpegBase64;
  }
  return imageBase64;
};
